import { getDaysSinceDate, getFreshnessStatus, FreshnessThresholds } from './scoutingUtils';
import { naturalCompare } from './naturalCompare';

export interface InspectionPointLike {
  pestId?: string | null;
  timestamp?: string | null;
}

export interface SummaryEntry {
  orchardId: string;
  blockId: string | null;
  orchardName: string | null;
  farmName: string | null;
  lastScoutedDate: string | null;
  lastScouterName: string | null;
  boundary: unknown;
  location: unknown;
  totalPoints: number;
  latestPoints: InspectionPointLike[];
  areaHectares: number | null;
  latestRecordId: string | null;
}

export interface ScoutingKpis {
  upToDate: number;
  pending: number;       // yellow + red
  overdue: number;       // red only
  inspectionPoints: number;
  totalOrchards: number;
  scouterCount: number;
}

export function computeScoutingKpis(summary: SummaryEntry[], thresholds: FreshnessThresholds): ScoutingKpis {
  let upToDate = 0;
  let pending = 0;
  let overdue = 0;
  let inspectionPoints = 0;
  const scouters = new Set<string>();
  for (const s of summary) {
    inspectionPoints += s.totalPoints ?? 0;
    if (s.lastScouterName) scouters.add(s.lastScouterName);
    const days = getDaysSinceDate(s.lastScoutedDate);
    const status = getFreshnessStatus(days, thresholds);
    if (status === 'green') upToDate += 1;
    else if (status === 'yellow') pending += 1;
    else if (status === 'red') { pending += 1; overdue += 1; }
  }
  return {
    upToDate,
    pending,
    overdue,
    inspectionPoints,
    totalOrchards: summary.length,
    scouterCount: scouters.size,
  };
}

export interface OverdueOrchard {
  orchardId: string;
  orchardName: string;
  farmName: string;
  daysSince: number;
  lastScouterName: string | null;
}

export function computeOverdueOrchards(summary: SummaryEntry[], thresholds: FreshnessThresholds, limit: number): OverdueOrchard[] {
  return summary
    .map(s => {
      const days = getDaysSinceDate(s.lastScoutedDate);
      const status = getFreshnessStatus(days, thresholds);
      return { s, days, status };
    })
    .filter(({ status, days }) => status === 'red' && days != null)
    .sort((a, b) => {
      const dDiff = (b.days ?? 0) - (a.days ?? 0);
      if (dDiff !== 0) return dDiff;
      return naturalCompare(a.s.orchardName ?? a.s.orchardId, b.s.orchardName ?? b.s.orchardId);
    })
    .slice(0, limit)
    .map(({ s, days }) => ({
      orchardId: s.orchardId,
      orchardName: s.orchardName ?? s.orchardId,
      farmName: s.farmName ?? '',
      daysSince: days as number,
      lastScouterName: s.lastScouterName,
    }));
}

export interface RecentDetection {
  orchardId: string;
  orchardName: string;
  pestId: string;
  timestamp: string;
}

export function computeRecentPestDetections(summary: SummaryEntry[], days: number, limit: number): RecentDetection[] {
  const cutoff = Date.now() - days * 86400000;
  const flat: RecentDetection[] = [];
  for (const s of summary) {
    for (const p of s.latestPoints ?? []) {
      if (!p.pestId || !p.timestamp) continue;
      const t = new Date(p.timestamp).getTime();
      if (t < cutoff) continue;
      flat.push({
        orchardId: s.orchardId,
        orchardName: s.orchardName ?? s.orchardId,
        pestId: p.pestId,
        timestamp: p.timestamp,
      });
    }
  }
  return flat
    .sort((a, b) => {
      const tDiff = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      if (tDiff !== 0) return tDiff;
      return naturalCompare(a.orchardName, b.orchardName);
    })
    .slice(0, limit);
}

export interface InactiveScouter {
  scouterName: string;
  lastActiveDays: number;
}

export function computeInactiveScouters(summary: SummaryEntry[], days: number): InactiveScouter[] {
  // Build scouter -> most-recent activity (in days)
  const map = new Map<string, number>();
  for (const s of summary) {
    if (!s.lastScouterName) continue;
    const d = getDaysSinceDate(s.lastScoutedDate);
    if (d == null) continue;
    const existing = map.get(s.lastScouterName);
    if (existing == null || d < existing) map.set(s.lastScouterName, d);
  }
  return Array.from(map.entries())
    .filter(([, d]) => d > days)
    .map(([scouterName, lastActiveDays]) => ({ scouterName, lastActiveDays }))
    .sort((a, b) => {
      const dDiff = b.lastActiveDays - a.lastActiveDays;
      if (dDiff !== 0) return dDiff;
      return naturalCompare(a.scouterName, b.scouterName);
    });
}
